// How big to render, and at what shape. Pure arithmetic — no DOM beyond the two
// `window` readers at the bottom — so the sizing rules are testable.
//
// Two callers with different needs share one rule:
//   - The generator renders at the VIEWPORT's aspect, because the drag selection
//     is locked to that aspect (see Display.lockedCorner), so bounds and pixels
//     agree and the image fills the screen without distortion.
//   - The gallery renders at the ENTRY's OWN bounds aspect, because a saved region
//     carries the aspect of whatever screen it was found on. Re-rendering a
//     landscape region into a portrait canvas would stretch the artwork into a
//     different picture, so we letterbox with CSS instead (object-fit: contain).

// Render resolution is capped for performance: the per-pixel escape maths runs in
// JS on the main thread, so cost scales with pixel count. The image is then
// CSS-scaled up to fill its container.
export const RENDER_CAP = 1000;

// ---- The whole set, in the complex plane --------------------------------------
// Generous bounds of the entire Mandelbrot set: the frame everything else is
// measured against. Lives here as the single copy — Display.js frames the opening
// view with it, format.js measures magnification against its width, and
// heroSpecimen.js starts the landing page's descent from it. It had drifted into
// three separate hardcoded 3.0s before those three callers needed to agree.
export const SET_CENTRE = { r: -0.5, i: 0.0 };
export const SET_WIDTH = 3.0;
export const SET_HEIGHT = 2.7;

// Frame the entire set inside a viewport aspect without distortion: fit whichever
// dimension is the binding constraint, centre on the set. Fitting rather than
// cropping is the same rule the gallery follows with object-fit: contain — a
// cropped region is a different picture, not a differently-sized one.
export const fitWholeSet = (aspect) => {
    const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
    let realSpan, imagSpan;
    if (safeAspect > SET_WIDTH / SET_HEIGHT) {
        imagSpan = SET_HEIGHT;
        realSpan = imagSpan * safeAspect;
    } else {
        realSpan = SET_WIDTH;
        imagSpan = realSpan / safeAspect;
    }
    return {
        realMin: SET_CENTRE.r - realSpan / 2,
        realMax: SET_CENTRE.r + realSpan / 2,
        imaginaryMin: SET_CENTRE.i - imagSpan / 2,
        imaginaryMax: SET_CENTRE.i + imagSpan / 2
    };
};

// The complex-plane shape of a region — the aspect its pixels must be rendered at
// for the artwork not to be stretched.
export const boundsAspect = ({ realMin, realMax, imaginaryMin, imaginaryMax }) => {
    const realSpan = realMax - realMin;
    const imaginarySpan = imaginaryMax - imaginaryMin;
    if (!(realSpan > 0) || !(imaginarySpan > 0)) return 1;
    return realSpan / imaginarySpan;
};

// The largest box of the given aspect that fits inside vw x vh, then capped so the
// longer side never exceeds `cap`.
export const fitDims = (aspect, vw, vh, cap = RENDER_CAP) => {
    const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;

    let width = vw;
    let height = vw / safeAspect;
    if (height > vh) {
        height = vh;
        width = vh * safeAspect;
    }

    const scale = Math.min(1, cap / Math.max(width, height));
    return {
        width: Math.max(1, Math.round(width * scale)),
        height: Math.max(1, Math.round(height * scale))
    };
};

// Dimensions of a given aspect at a target PIXEL COUNT rather than a fixed
// width/height, so a wide region and a tall region cost the same to render — the
// render-cost trap in PHASE-2-PLAN.md is about pixel count, not dimensions. Every
// caller that renders something small budgets it this way.
export const dimsForPixels = (aspect, pixels) => {
    const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
    const height = Math.max(8, Math.round(Math.sqrt(pixels / safeAspect)));
    return { width: Math.max(8, Math.round(height * safeAspect)), height };
};

// Gallery index thumbnails. ~14k pixels is roughly 2x the 74x47 display size
// (crisp on retina) and ~50x cheaper than a full-screen render.
export const THUMB_TARGET_PIXELS = 14000;

export const thumbDims = (aspect) => dimsForPixels(aspect, THUMB_TARGET_PIXELS);

// The generator's canvas: the viewport's own aspect, capped.
export const computeCanvasDimensions = () => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return fitDims(vw / vh, vw, vh);
};

// A saved entry rendered as large as the viewport usefully allows, in its own
// shape. Never larger than the entry needs, never larger than the cap.
export const entryRenderDims = (entry) =>
    fitDims(boundsAspect(entry), window.innerWidth, window.innerHeight);
